import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, Sparkles, MessageSquare, Brain, Code, Palette, FileText, BarChart3, Lightbulb, Shield, Clock, Zap, Target, Heart, Globe, Mic } from 'lucide-react';
import { GlassCard, GlassButton, FadeIn } from '../../components/ui';

const personalities = [
  { id: 1, name: 'Nexus Assistant', role: 'Assistente Geral', icon: Bot, color: '#6366f1', description: 'Assistente virtual para tarefas diárias', active: true },
  { id: 2, name: 'Code Nexus', role: 'Desenvolvedor', icon: Code, color: '#22c55e', description: 'Especialista em código e automação', active: true },
  { id: 3, name: 'Creative Nexus', role: 'Designer', icon: Palette, color: '#ec4899', description: 'Criação de designs e materiais visuais', active: true },
  { id: 4, name: 'Analyst Nexus', role: 'Analista', icon: BarChart3, color: '#f59e0b', description: 'Análise de dados e relatórios', active: true },
  { id: 5, name: 'Writer Nexus', role: 'Redator', icon: FileText, color: '#3b82f6', description: 'Criação de conteúdo e copywriting', active: false },
  { id: 6, name: 'Strategist Nexus', role: 'Estrategista', icon: Target, color: '#8b5cf6', description: 'Planejamento estratégico de negócios', active: true },
  { id: 7, name: 'Guard Nexus', role: 'Segurança', icon: Shield, color: '#ef4444', description: 'Monitoramento e segurança', active: false },
  { id: 8, name: 'Time Nexus', role: 'Produtividade', icon: Clock, color: '#14b8a6', description: 'Gestão de tempo e produtividade', active: true },
  { id: 9, name: 'Fast Nexus', role: 'Performance', icon: Zap, color: '#f97316', description: 'Otimização de processos', active: false },
  { id: 10, name: 'Idea Nexus', role: 'Inovação', icon: Lightbulb, color: '#eab308', description: 'Geração de ideias e inovação', active: true },
  { id: 11, name: 'Social Nexus', role: 'Comunicação', icon: Heart, color: '#ec4899', description: 'Comunicação e relacionamento', active: false },
  { id: 12, name: 'Global Nexus', role: 'Tradução', icon: Globe, color: '#06b6d4', description: 'Tradução e localização', active: false },
  { id: 13, name: 'Voice Nexus', role: 'Áudio', icon: Mic, color: '#a855f7', description: 'Processamento de áudio e voz', active: false },
  { id: 14, name: 'Smart Nexus', role: 'Consultor', icon: Brain, color: '#6366f1', description: 'Consultoria especializada', active: true },
];

const tabs = [
  { id: 'personas', label: 'Personas' },
  { id: 'chat', label: 'Chat Multi-Agente' },
  { id: 'config', label: 'Configurações' },
];

export const NexusTeam = () => {
  const [activeTab, setActiveTab] = useState('personas');
  const [selectedPersonality, setSelectedPersonality] = useState(null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">NexusTeam</h1>
          <p className="text-white/50">14 personalidades de IA à sua disposição</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm text-white/60">14 agentes online</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 text-white border border-white/10' 
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <FadeIn>
        {activeTab === 'personas' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {personalities.map((persona) => {
              const Icon = persona.icon;
              return (
                <motion.div
                  key={persona.id}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPersonality(persona)}
                  className={`p-5 rounded-2xl border cursor-pointer transition-all ${
                    persona.active 
                      ? 'bg-slate-900/60 backdrop-blur-xl border-white/10 hover:border-white/20' 
                      : 'bg-slate-900/30 border-white/5 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${persona.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: persona.color }} />
                    </div>
                    {persona.active && (
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                    )}
                  </div>
                  <h4 className="font-semibold text-white/90">{persona.name}</h4>
                  <p className="text-sm text-white/50 mb-2">{persona.role}</p>
                  <p className="text-xs text-white/40">{persona.description}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {activeTab === 'chat' && (
          <GlassCard className="h-[600px] flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center gap-3">
              <div className="flex -space-x-2">
                {personalities.slice(0, 4).map((p) => (
                  <div
                    key={p.id}
                    className="w-8 h-8 rounded-full border-2 border-slate-900 flex items-center justify-center"
                    style={{ backgroundColor: `${p.color}30` }}
                  >
                    <p.icon className="w-4 h-4" style={{ color: p.color }} />
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-slate-900 bg-white/10 flex items-center justify-center text-xs text-white/60">
                  +10
                </div>
              </div>
              <div>
                <p className="font-medium text-white/90">Chat Multi-Agente</p>
                <p className="text-xs text-white/50">Múltiplas personalidades respondendo</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-center text-white/40">Chat com o NexusTeam</p>
            </div>
          </GlassCard>
        )}

        {activeTab === 'config' && (
          <GlassCard className="p-6">
            <p className="text-white/50">Configurações do NexusTeam em desenvolvimento</p>
          </GlassCard>
        )}
      </FadeIn>
    </div>
  );
};

export default NexusTeam;
